import { cs } from '../../../lib/currency';
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { CreditCard, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';

/**
 * Combined Stripe Checkout link covering many lane bookings for the same
 * customer. Mounts the bundle on the backend and (optionally) emails the
 * link to the customer.
 *
 * Props:
 *   open, onOpenChange   — dialog control
 *   bookings             — eligible lane_booking rows (already filtered)
 *   onDone               — fired after admin closes the success state
 */
export default function CombinedPaymentLinkDialog({ open, onOpenChange, bookings, onDone }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [customMsg, setCustomMsg] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');

  useEffect(() => {
    if (open) {
      setResult(null);
      setSendEmail(true);
      setCustomMsg('');
      setDiscountPercent('');
      setDiscountAmount('');
      setDiscountReason('');
    }
  }, [open]);

  const totalAmount = (bookings || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const submit = async () => {
    setSending(true);
    try {
      const booking_ids = (bookings || []).map((r) => r.id);
      const { data } = await api.post('/lanes/admin/bookings/bulk-send-payment-link', {
        booking_ids,
        send_email: sendEmail,
        custom_message: customMsg || null,
        discount_percent: discountPercent === '' ? null : Number(discountPercent),
        discount_amount: discountAmount === '' ? null : Number(discountAmount),
        price_adjustment_reason: discountReason || null,
      });
      setResult(data);
      toast.success(
        `Combined link created — ${cs()}${data.total_amount.toFixed(2)} for ${data.item_count} booking${data.item_count === 1 ? '' : 's'}.${data.email_sent ? ' Email sent.' : ''}`,
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create combined payment link');
    } finally {
      setSending(false);
    }
  };

  const handleClose = (open_) => {
    if (sending) return;
    onOpenChange(open_);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="combined-payment-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Send Combined Payment Link
          </DialogTitle>
          <DialogDescription>
            Bundle multiple lane bookings into a single Stripe Checkout
            link and optionally email it to the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {!result ? (
            <>
              <p className="text-slate-600">
                Bundles {bookings?.length || 0} lane booking{(bookings?.length || 0) === 1 ? '' : 's'} into
                a single Stripe Checkout link. Paying it marks every booking
                paid in one transaction.
              </p>
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-600">Bookings</span>
                  <span className="font-semibold" data-testid="combined-link-count">{bookings?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Total amount</span>
                  <span className="font-semibold">{cs()}{totalAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    data-testid="combined-link-send-email"
                  />
                  Email the link to the customer
                </Label>
                <details className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="combined-link-discount-block">
                  <summary className="text-xs font-medium text-slate-700 cursor-pointer select-none">
                    Apply discount to all bookings (optional)
                  </summary>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label className="text-xs">Discount %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={discountPercent}
                        onChange={(e) => setDiscountPercent(e.target.value)}
                        className="h-8"
                        data-testid="combined-link-discount-percent"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Discount $ (per booking)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(e.target.value)}
                        className="h-8"
                        data-testid="combined-link-discount-amount"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Reason</Label>
                      <Input
                        value={discountReason}
                        onChange={(e) => setDiscountReason(e.target.value)}
                        placeholder="Audit log entry"
                        className="h-8"
                        data-testid="combined-link-discount-reason"
                      />
                    </div>
                  </div>
                </details>
                <Textarea
                  value={customMsg}
                  onChange={(e) => setCustomMsg(e.target.value)}
                  placeholder="Optional custom message to include in the email"
                  rows={2}
                  data-testid="combined-link-custom-msg"
                />
              </div>
            </>
          ) : (
            <div className="space-y-3" data-testid="combined-link-result">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 space-y-1.5">
                <p className="font-semibold text-emerald-700">
                  Link created — {cs()}{result.total_amount.toFixed(2)} for {result.item_count} booking{result.item_count === 1 ? '' : 's'}
                </p>
                {result.email_sent ? (
                  <p className="text-xs text-emerald-700">✓ Emailed to customer</p>
                ) : (
                  <p className="text-xs text-slate-600">Email not sent — copy and share the link below.</p>
                )}
              </div>
              <div className="border rounded-md p-2 text-xs break-all bg-slate-50 font-mono" data-testid="combined-link-url">
                {result.checkout_url}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(result.checkout_url);
                  toast.success('Link copied');
                }}
              >
                Copy link
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          {!result ? (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={sending}
                data-testid="combined-link-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="combined-link-submit"
              >
                {sending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
                ) : (<>Create Combined Link</>)}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => { onOpenChange(false); onDone && onDone(); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="combined-link-close"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
